import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import PricingPage from './pages/PricingPage';
import UseCasesPage from './pages/UseCasesPage';
import FeaturesPage from './pages/FeaturesPage';

const { FiSearch, FiTool, FiEdit3, FiArrowRight, FiMenu, FiX, FiTwitter, FiLinkedin, FiExternalLink } = FiIcons;

function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const handleStartCloning = () => {
    // You can replace this with your actual cloning functionality
    alert('🚀 Welcome to ClonewebX! This would normally open the cloning interface. For now, this is a demo.');
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="text-2xl font-bold text-slate-800">
              Clone<span className="text-purple-600">webX</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/features" 
              className={`font-medium transition-colors ${isActive('/features') ? 'text-purple-600' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Features
            </Link>
            <Link 
              to="/use-cases" 
              className={`font-medium transition-colors ${isActive('/use-cases') ? 'text-purple-600' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Use Cases
            </Link>
            <Link 
              to="/pricing" 
              className={`font-medium transition-colors ${isActive('/pricing') ? 'text-purple-600' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Pricing
            </Link>
            <button 
              onClick={() => alert('📝 Blog section coming soon!')} 
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
            >
              Blog
            </button>
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <button 
              onClick={() => alert('🔐 Login functionality would be implemented here')}
              className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
            >
              Login
            </button>
            <button 
              onClick={handleStartCloning}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
            >
              Start Cloning Free
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6 text-slate-600" />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white border-t border-slate-100 py-4"
          >
            <div className="flex flex-col space-y-4">
              <Link 
                to="/features" 
                className="text-slate-600 hover:text-slate-900 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Features
              </Link>
              <Link 
                to="/use-cases" 
                className="text-slate-600 hover:text-slate-900 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Use Cases
              </Link>
              <Link 
                to="/pricing" 
                className="text-slate-600 hover:text-slate-900 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Pricing
              </Link>
              <button 
                onClick={() => {
                  alert('📝 Blog section coming soon!');
                  setIsMenuOpen(false);
                }}
                className="text-slate-600 hover:text-slate-900 font-medium text-left"
              >
                Blog
              </button>
              <div className="pt-4 border-t border-slate-100">
                <button 
                  onClick={() => {
                    alert('🔐 Login functionality would be implemented here');
                    setIsMenuOpen(false);
                  }}
                  className="text-slate-600 hover:text-slate-900 font-medium mb-3 block"
                >
                  Login
                </button>
                <button 
                  onClick={() => {
                    handleStartCloning();
                    setIsMenuOpen(false);
                  }}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-purple-700 transition-colors w-full"
                >
                  Start Cloning Free
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
}

function HomePage() {
  const navigate = useNavigate();
  
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const handleCloneSite = () => {
    // This would normally open a modal or redirect to the cloning interface
    const userUrl = prompt('🌐 Enter the URL of the website you want to clone:', 'https://');
    if (userUrl && userUrl !== 'https://') {
      alert(`🚀 Cloning ${userUrl}...\n\nThis is a demo. In the real app, this would:\n1. Analyze the website\n2. Extract the HTML/CSS\n3. Open the visual editor\n4. Allow you to export to your platform of choice`);
    }
  };

  const handleGetStarted = () => {
    // Navigate to pricing page or show sign up modal
    navigate('/pricing');
  };

  return (
    <>
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial="initial"
            animate="animate"
            variants={stagger}
          >
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 mb-6 leading-tight"
              variants={fadeInUp}
            >
              The fastest way to clone a website{' '}
              <span className="text-purple-600">without coding</span>
            </motion.h1>
            
            <motion.p 
              className="text-xl text-slate-600 mb-8 leading-relaxed max-w-3xl mx-auto"
              variants={fadeInUp}
            >
              ClonewebX lets you clone and customize websites or transfer elements between page builders. 
              Copy and save parts of a site, then adjust the UI and UX in minutes.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-4"
              variants={fadeInUp}
            >
              <button 
                onClick={handleCloneSite}
                className="bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-700 transition-all transform hover:scale-105 flex items-center gap-2"
              >
                Clone a Site for Free
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
            </motion.div>
            
            <motion.p className="text-slate-500 text-sm" variants={fadeInUp}>
              No credit card required
            </motion.p>

            {/* App Screenshot Placeholder */}
            <motion.div className="mt-16 relative" variants={fadeInUp}>
              <div className="bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl p-8 shadow-2xl">
                <div className="bg-white rounded-lg p-6 shadow-lg">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  </div>
                  <div className="space-y-4">
                    <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                    <div className="h-3 bg-slate-100 rounded w-full"></div>
                    <div className="h-3 bg-slate-100 rounded w-5/6"></div>
                    <div className="bg-purple-100 rounded-lg p-4 border-2 border-purple-300 border-dashed">
                      <div className="text-center text-purple-600 font-semibold">
                        Cloning in progress... 87%
                      </div>
                      <div className="w-full bg-purple-200 rounded-full h-2 mt-2">
                        <div className="bg-purple-600 h-2 rounded-full w-[87%]"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-slate-600 font-medium mb-8">Trusted by over 10,000 designers and developers</p>
            <div className="flex justify-center items-center gap-8 flex-wrap opacity-60">
              {['DesignStudio', 'WebAgency', 'FastLane Devs', 'PixelPerfect', 'CreativeHub'].map((company, index) => (
                <div key={index} className="text-slate-400 font-semibold text-lg">
                  {company}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Benefits */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              How does it make your life easier?
            </h2>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div className="text-center p-8 rounded-2xl hover:shadow-lg transition-shadow" variants={fadeInUp}>
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiSearch} className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Capture Any Design</h3>
              <p className="text-slate-600 leading-relaxed">
                Point our smart cloner at any live website. Get a perfect copy of the structure, design, and even animations.
              </p>
            </motion.div>

            <motion.div className="text-center p-8 rounded-2xl hover:shadow-lg transition-shadow" variants={fadeInUp}>
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiTool} className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Export to Your Workflow</h3>
              <p className="text-slate-600 leading-relaxed">
                Export the cloned code to Webflow, Framer, or as clean HTML/CSS. Or, copy individual elements directly into your page builder.
              </p>
            </motion.div>

            <motion.div className="text-center p-8 rounded-2xl hover:shadow-lg transition-shadow" variants={fadeInUp}>
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiEdit3} className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Customize with Ease</h3>
              <p className="text-slate-600 leading-relaxed">
                Our built-in editor lets you tweak the cloned design before you export. Change colors, fonts, and content instantly.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              From clone to launch in three simple steps
            </h2>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            {[
              { step: '1', title: 'Enter the URL', desc: 'Paste the link of the website you want to clone.' },
              { step: '2', title: 'Review & Edit', desc: 'Use our visual editor to customize the cloned design to your needs.' },
              { step: '3', title: 'Export & Publish', desc: 'Send the final design to your favorite platform or download the code.' }
            ].map((item, index) => (
              <motion.div key={index} className="relative text-center" variants={fadeInUp}>
                <div className="w-12 h-12 bg-purple-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-4">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.desc}</p>
                {index < 2 && (
                  <div className="hidden md:block absolute top-6 left-1/2 w-full h-0.5 bg-slate-200 -z-10"></div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="use-cases" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              How our customers use ClonewebX
            </h2>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div className="bg-white p-8 rounded-2xl shadow-lg" variants={fadeInUp}>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                "I use ClonewebX to save inspiring layouts I find into my personal library. It has cut my design research time in half."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-blue-400 rounded-full flex items-center justify-center text-white font-semibold">
                  AR
                </div>
                <div>
                  <div className="font-semibold text-slate-800">Alex R.</div>
                  <div className="text-slate-500">Product Designer</div>
                </div>
              </div>
            </motion.div>

            <motion.div className="bg-white p-8 rounded-2xl shadow-lg" variants={fadeInUp}>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                "My clients always want changes. Now I can clone a section they like from another site and customize it for them in minutes, not hours."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-blue-400 rounded-full flex items-center justify-center text-white font-semibold">
                  MK
                </div>
                <div>
                  <div className="font-semibold text-slate-800">Maya K.</div>
                  <div className="text-slate-500">Freelancer</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-slate-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              Stop building from scratch. Start cloning.
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Join thousands of savvy developers and designers who are accelerating their workflow.
            </p>
            <button 
              onClick={handleGetStarted}
              className="bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-700 transition-all transform hover:scale-105 flex items-center gap-2 mx-auto"
            >
              Get Started for Free
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>
    </>
  );
}

function Footer() {
  const handleFooterClick = (section) => {
    alert(`📋 ${section} section coming soon!`);
  };

  return (
    <footer className="bg-slate-800 text-white py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Logo and Tagline */}
          <div>
            <div className="text-2xl font-bold mb-4">
              Clone<span className="text-purple-400">webX</span>
            </div>
            <p className="text-slate-400">Clone. Customize. Launch.</p>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="font-semibold mb-4">Product</h4>
            <ul className="space-y-2 text-slate-400">
              <li><Link to="/features" className="hover:text-white transition-colors">Features</Link></li>
              <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="font-semibold mb-4">Resources</h4>
            <ul className="space-y-2 text-slate-400">
              <li><Link to="/use-cases" className="hover:text-white transition-colors">Use Cases</Link></li>
              <li><button onClick={() => handleFooterClick('Blog')} className="hover:text-white transition-colors text-left">Blog</button></li>
              <li><button onClick={() => handleFooterClick('Documentation')} className="hover:text-white transition-colors text-left">Docs</button></li>
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-slate-400">
              <li><button onClick={() => handleFooterClick('About')} className="hover:text-white transition-colors text-left">About</button></li>
              <li><button onClick={() => handleFooterClick('Contact')} className="hover:text-white transition-colors text-left">Contact</button></li>
            </ul>
          </div>
        </div>

        {/* Social Media */}
        <div className="flex justify-center gap-6 mb-8">
          <button onClick={() => handleFooterClick('Twitter')} className="text-slate-400 hover:text-white transition-colors">
            <SafeIcon icon={FiTwitter} className="w-6 h-6" />
          </button>
          <button onClick={() => handleFooterClick('LinkedIn')} className="text-slate-400 hover:text-white transition-colors">
            <SafeIcon icon={FiLinkedin} className="w-6 h-6" />
          </button>
          <button onClick={() => handleFooterClick('Website')} className="text-slate-400 hover:text-white transition-colors">
            <SafeIcon icon={FiExternalLink} className="w-6 h-6" />
          </button>
        </div>

        {/* Legal */}
        <div className="border-t border-slate-700 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-400 text-sm">
            © 2023 ClonewebX. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-slate-400">
            <button onClick={() => handleFooterClick('Privacy Policy')} className="hover:text-white transition-colors">Privacy Policy</button>
            <button onClick={() => handleFooterClick('Terms of Service')} className="hover:text-white transition-colors">Terms of Service</button>
          </div>
        </div>
      </div>
    </footer>
  );
}

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-white font-['Plus_Jakarta_Sans']">
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/use-cases" element={<UseCasesPage />} />
        </Routes>
        <Footer />
      </div>
    </Router>
  );
}

export default App;